import {
  AdminClearInvoiceActionRun,
  AdminFetchInvoiceDueInterest,
  AdminFetchInvoices,
  AdminRunInvoiceAction,
  AdminSetInvoiceActionProgress,
} from 'src/constants/actionTypes'
import type {
  InvoiceActionProgressPayload,
  InvoiceActionResult,
  InvoiceDueInterest,
  InvoicesPage,
  RunInvoiceActionPayload,
} from 'src/interfaces/invoice'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { InvoiceState } from './types'

const getInitialState = (): InvoiceState => {
  return {
    composedInvoices: [],
    pagination: {},
    invoicesLoading: false,
    invoicesError: null,
    dueInterest: null,
    dueInterestLoading: false,
    dueInterestError: null,
    actionRun: null,
  }
}

export const invoiceReducer = (
  state: InvoiceState = getInitialState(),
  action: Action<unknown>,
): InvoiceState => {
  switch (action.type) {
    // Unlike markets, this deliberately does NOT clear `composedInvoices`: paging holds the
    // previous page under the grid's loading overlay instead of collapsing to an empty state
    // and back on every page change.
    case AdminFetchInvoices.type.REQUEST:
      return {
        ...state,
        invoicesLoading: true,
        invoicesError: null,
      }
    case AdminFetchInvoices.type.SUCCESS: {
      const page = action.payload as InvoicesPage | undefined

      return {
        ...state,
        composedInvoices: page?.data ?? [],
        pagination: page?.pagination ?? {},
        invoicesLoading: false,
        invoicesError: null,
      }
    }
    case AdminFetchInvoices.type.FAILED:
      return {
        ...state,
        composedInvoices: [],
        pagination: {},
        invoicesLoading: false,
        invoicesError: resolveErrorMessage(action.payload, 'Failed to load invoices'),
      }

    case AdminFetchInvoiceDueInterest.type.REQUEST:
      return {
        ...state,
        dueInterestLoading: true,
        dueInterestError: null,
      }
    case AdminFetchInvoiceDueInterest.type.SUCCESS: {
      // `null` is what the endpoint answers when Oatfi reports nothing due. It is stored as
      // `null` and rendered as an empty state — never coerced to a zero the backend did not say.
      const dueInterest = (action.payload as InvoiceDueInterest | null | undefined) ?? null

      return {
        ...state,
        dueInterest,
        dueInterestLoading: false,
        dueInterestError: null,
      }
    }
    case AdminFetchInvoiceDueInterest.type.FAILED:
      return {
        ...state,
        dueInterestLoading: false,
        dueInterestError: resolveErrorMessage(action.payload, 'Failed to load due interest'),
      }

    case AdminRunInvoiceAction.type.REQUEST: {
      const payload = action.payload as RunInvoiceActionPayload

      // A retry re-enters here, which is what resets the modal from its results step back to
      // progress — the run is replaced wholesale, never appended to.
      return {
        ...state,
        actionRun: {
          verb: payload.verb,
          composedInvoiceLabel: payload.composedInvoiceLabel,
          total: payload.invoiceIds.length,
          completed: 0,
          isRunning: true,
          results: [],
        },
      }
    }
    case AdminSetInvoiceActionProgress.type: {
      if (!state.actionRun) {
        return state
      }

      const progress = action.payload as InvoiceActionProgressPayload

      return {
        ...state,
        actionRun: {
          ...state.actionRun,
          completed: progress.completed,
          results: progress.results,
        },
      }
    }
    case AdminRunInvoiceAction.type.SUCCESS: {
      if (!state.actionRun) {
        return state
      }

      const results = (action.payload as InvoiceActionResult[] | undefined) ?? []

      return {
        ...state,
        actionRun: {
          ...state.actionRun,
          isRunning: false,
          results,
        },
      }
    }
    case AdminRunInvoiceAction.type.FAILED: {
      if (!state.actionRun) {
        return state
      }

      return {
        ...state,
        actionRun: {
          ...state.actionRun,
          isRunning: false,
        },
      }
    }
    case AdminClearInvoiceActionRun.type:
      return {
        ...state,
        actionRun: null,
      }

    default:
      return state
  }
}
